import { cn } from "@gigpro/ui/tailwind/cn";
import { useBreakpoint } from "@gigpro/ui/utils/useBreakpoint";
import { CatchBoundary, useRouterState } from "@tanstack/react-router";
import { ContentCards } from "components/ContentCards";
import { ErrorBoundary } from "components/ErrorBoundary";
import { EmojiLoader } from "components/Loader/emoji";
import React from "react";
import { useActiveLocationId } from "services/locationOrgContext";
import { useRouteTracker } from "utils/analytics";

export type BaseLayoutSlots = {
  mobileHeader?: React.ReactNode;
  desktopHeader?: React.ReactNode;
  topNavigation?: React.ReactNode;
  bottomNavigation?: React.ReactNode;
  backButton?: React.ReactNode;
  announcementBanner?: React.ReactNode;
  sidebar?: { left?: React.ReactNode; right?: React.ReactNode };
};

export type BaseLayoutProps = React.PropsWithChildren<{
  mainNav: React.ReactNode;
  bottomNav?: React.ReactNode;
  slots?: BaseLayoutSlots;
  title: string;
  subtitle?: string;
  className?: string;
  containerClassName?: string;
  contentClassName?: string;
  headerClassName?: string;
}>;

export const BaseLayout: React.FC<BaseLayoutProps> = ({
  children,
  mainNav,
  bottomNav,
  slots,
  title,
  subtitle,
  className,
  containerClassName,
  contentClassName,
  headerClassName,
}) => {
  useRouteTracker();
  const isDesktop = useBreakpoint("md");
  const href = useRouterState({ select: (state) => state.location.href });
  const [activeLocationId] = useActiveLocationId();

  React.useEffect(() => {
    const brandName = "Qwick";
    document.title = title ? `${title} | ${brandName}` : brandName;
  }, [title]);

  return (
    <div className={cn("flex min-h-dvh flex-col pt-nav-top", className)}>
      {mainNav}
      <main
        className={cn(
          "relative flex min-w-0 flex-1 flex-col bg-white pb-nav-bottom md:flex-row md:bg-background md:pb-0",
          containerClassName,
        )}
      >
        {isDesktop && slots?.sidebar?.left && (
          <div className="sticky top-nav-top z-10 h-[calc(100dvh-var(--spacing-nav-top))]">{slots.sidebar.left}</div>
        )}

        <div className={cn("flex w-full flex-1 flex-col p-2 md:p-4 xl:mx-auto xl:max-w-7xl", contentClassName)}>
          {slots?.announcementBanner}
          {isDesktop && (
            <div className={cn("flex items-center justify-between pt-4 pb-6", headerClassName)}>
              <div>
                {slots?.backButton}
                <h1 className="text-screenTitle font-semibold" data-testid="title">
                  {title}
                </h1>
                <p className="text-normal">{subtitle}</p>
              </div>
              <div className="flex h-8 flex-1 flex-row items-center justify-end gap-x-2">{slots?.desktopHeader}</div>
            </div>
          )}
          <CatchBoundary getResetKey={() => `${href}|${activeLocationId}`} errorComponent={ErrorBoundary}>
            <React.Suspense fallback={<EmojiLoader />}>{children}</React.Suspense>
          </CatchBoundary>
        </div>

        {isDesktop && slots?.sidebar?.right && (
          <div className="sticky top-nav-top z-10 h-[calc(100dvh-var(--spacing-nav-top))]">{slots.sidebar.right}</div>
        )}
        {isDesktop && (
          <div className="sticky top-nav-top h-[calc(100dvh-var(--spacing-nav-top))]">
            <ContentCards />
          </div>
        )}
      </main>
      {!isDesktop && (bottomNav ?? slots?.bottomNavigation)}
    </div>
  );
};
